import React from "react";
import Select from "react-select";
import styled, { useTheme } from "styled-components";

type OptionType = {
  label: string;
  value: string;
};

interface SearchableSelectProps {
  list: {
    label: string;
    value: string;
  }[];
  placeholder?: string;
  onValueChange?: (value: string) => void;
}

const SearchableSelect = ({
  list,
  placeholder,
  onValueChange,
}: SearchableSelectProps) => {
  const theme = useTheme();

  return (
    <SelectStyle
      options={list}
      placeholder={placeholder}
      onChange={(option) => {
        if (onValueChange) onValueChange(option ? option.value : "");
      }}
      styles={{
        container: (styles) => ({
          ...styles,
          outline: "none",
        }),
        control: (styles, { isFocused }) => ({
          ...styles,
          display: "flex",
          alignItems: "center",
          gap: "0.8rem",
          height: "4.6rem",
          padding: "0 0.4rem",
          border: `0.1rem solid ${theme.color.stroke.light}`,
          borderRadius: "0.5rem",
          borderColor: isFocused ? "transparent" : theme.color.stroke.light,
          boxShadow: "none",
          ":hover": {
            borderColor: theme.color.stroke.dark,
          },
        }),
        input: (styles) => ({
          ...styles,
          ...theme.typography.body1.regular,
        }),
        placeholder: (styles) => ({
          ...styles,
          color: theme.color.text.placeholder,
        }),
        menu: (styles) => ({
          ...styles,
          borderRadius: 0,
        }),
        menuList: (styles) => ({
          ...styles,
          padding: 0,
          borderRadius: 0,
        }),
        option: (styles, { isSelected, isFocused, isDisabled }) => {
          return {
            ...styles,
            backgroundColor: isFocused
              ? theme.color.background.dark
              : theme.color.background.white,
            color: isFocused
              ? theme.color.text.highEmphasis
              : theme.color.text.secondary,
            display: "flex",
            height: "4.8rem",
            padding: "1.2rem 1.4rem",
            alignItems: "center",
            gap: "1rem",

            ":active": {
              ...styles[":active"],
              backgroundColor: !isDisabled
                ? isSelected
                  ? theme.color.background.dark
                  : theme.color.background.white
                : undefined,
            },
          };
        },
        noOptionsMessage: (styles) => ({
          ...styles,
          display: "flex",
          height: "4.8rem",
          padding: "1.2rem 1.4rem",
          alignItems: "center",
          gap: "1rem",
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
      }}
    />
  );
};

export default SearchableSelect;

const SelectStyle = styled(Select<OptionType>)`
  svg {
    color: ${({ theme }) => theme.color.icon.secondary};
  }
`;
